import axios from 'axios';
import { rootUrl } from '../../config/config';

// Fired when the session can no longer be refreshed; AuthContext logs out on it,
// which sends ProtectedRoute pages to /login.
export const SESSION_EXPIRED_EVENT = 'pawafleet:session-expired';
// Fired with { detail: accessToken } after a successful refresh so AuthContext can sync its state.
export const TOKEN_REFRESHED_EVENT = 'pawafleet:token-refreshed';

/**
 * The backend answers a missing token with 401 and an expired or invalid one with
 * 403 { error: 'Invalid access token' }. Other 403s are permission denials and must
 * not trigger a refresh.
 */
export const isAccessTokenRejected = (error) => {
    const status = error?.response?.status;
    if (status === 401) return true;
    return status === 403 && error?.response?.data?.error === 'Invalid access token';
};

const isTokenEndpoint = (config) => /\/token(\/revoke)?$/.test(String(config?.url || '').split('?')[0]);

let refreshInFlight = null;

/**
 * Exchange the stored refresh token for a new access token. Concurrent callers share
 * one request, so a burst of rejected calls triggers a single refresh.
 * @returns {Promise<string>} The new access token
 */
export const refreshAccessToken = () => {
    if (!refreshInFlight) {
        refreshInFlight = (async () => {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) throw new Error('No refresh token found');
            const { data } = await axios.post(`${rootUrl}/token`, { token: refreshToken });
            if (!data?.accessToken) throw new Error('Token refresh failed');
            localStorage.setItem('authToken', data.accessToken);
            window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT, { detail: data.accessToken }));
            return data.accessToken;
        })().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

/** Revoke a refresh token on the server. Never throws: logout must always complete locally. */
export const revokeRefreshToken = (token) => {
    if (!token) return Promise.resolve();
    return axios.post(`${rootUrl}/token/revoke`, { token }).catch(() => {});
};

export const notifySessionExpired = () => {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

/**
 * Axios response error handler: on a rejected access token, refresh once and retry
 * the request; if the refresh fails, end the session.
 * @param {Function} client - The axios instance to retry with
 */
export const createAuthErrorHandler = (client) => async (error) => {
    const original = error?.config;
    if (!original || original._authRetry || isTokenEndpoint(original) || !isAccessTokenRejected(error)) {
        return Promise.reject(error);
    }
    original._authRetry = true;

    // A request sent with the old token can be rejected after another call already
    // refreshed it; retry with the current token instead of refreshing again.
    const sentToken = String(original.headers?.Authorization || original.headers?.authorization || '').replace(/^Bearer\s+/i, '');
    const currentToken = localStorage.getItem('authToken');
    let accessToken;
    try {
        accessToken = currentToken && sentToken && currentToken !== sentToken ? currentToken : await refreshAccessToken();
    } catch (refreshError) {
        console.warn('Session expired, signing out:', refreshError.message);
        notifySessionExpired();
        return Promise.reject(error);
    }

    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${accessToken}`;
    return client(original);
};
